<template>
  <article class="bg-white border rounded shadow-sm mb-3 p-3 position-relative">
    <div class="d-flex align-items-start justify-content-between">
      <div class="d-flex align-items-center min-w-0">
        <v-tooltip v-if="canMove" bottom>
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="visit-card__drag-grip"
              :class="{ 'visit-card__drag-grip--disabled': isMoving }"
              :disabled="isMoving"
              aria-label="Премести сервизно посещение"
              v-bind="attrs"
              v-on="on"
              @pointerdown.stop="$emit('drag-start', $event)"
            >
              <v-icon small>mdi-drag</v-icon>
            </button>
          </template>
          <span>Премести сервизно посещение</span>
        </v-tooltip>
        <div class="text-dark font-size-sm font-weight-bold min-w-0">
          {{ machineName }}
        </div>
      </div>
      <div class="d-flex flex-shrink-0 ml-2">
        <router-link
          v-if="checkIfUserHasPermission(['service-visits-show', 'mechanic'])"
          :to="{ name: 'show-service-visits', params: { id: visit.id } }"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("LABELS.VIEW") }}</span>
          </v-tooltip>
        </router-link>
        <router-link
          v-if="canEditVisit"
          :to="{ name: 'edit-service-visits', params: { id: visit.id } }"
        >
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $t("LABELS.EDIT") }}</span>
          </v-tooltip>
        </router-link>
      </div>
    </div>

    <div class="d-flex flex-wrap align-items-center font-size-xs mt-2">
      <span
        v-if="visit.priority"
        class="d-inline-flex align-items-center mr-2"
      >
        <span
          class="visit-card__priority-dot label label-dot"
          :style="{ backgroundColor: priorityColor }"
        ></span>
      </span>
      <span class="mr-3">{{ formattedDateRange }}</span>
      <span class="d-inline-flex align-items-center mr-3">
        <v-icon small class="mr-1">mdi-map-marker-outline</v-icon>
        <span>{{ locationText || "-" }}</span>
      </span>
      <span
        v-if="visit.duration !== null && visit.duration !== undefined"
        class="d-inline-flex align-items-center mr-3"
      >
        <v-icon small class="mr-1">mdi-clock-outline</v-icon>
        <span>{{ visit.duration }} {{ $t("SERVICE_VISITS.HOURS_SHORT") }}</span>
      </span>
      <span
        v-if="visit.serviceMileage !== null && visit.serviceMileage !== undefined"
        class="d-inline-flex align-items-center mr-3"
      >
        <v-icon small class="mr-1">mdi-speedometer</v-icon>
        <span>{{ visit.serviceMileage }} {{ visit.machine && visit.machine.isMileage ? $t("LABELS.KM") : $t("SERVICE_VISITS.MOTO_HOURS_SHORT") }}</span>
      </span>
      <span class="d-inline-flex align-items-center">
        <v-icon small class="mr-1">mdi-account-hard-hat</v-icon>
        <span>{{ mechanicNames || "-" }}</span>
      </span>
    </div>
    <div class="d-flex justify-content-start mt-3">
      <ServiceVisitBoardCardTasks
        :visit-id="visit.id"
        :items="visit.items || []"
        :item-statuses="itemStatuses"
        :can-edit="canEditTasks"
        :can-manage-task-structure="canManageTaskStructure"
        @task-updated="$emit('task-updated', $event)"
        @task-created="$emit('task-created', $event)"
        @tasks-reordered="$emit('tasks-reordered', $event)"
      />
    </div>
  </article>
</template>

<script>
import { permissionMixin } from "@/core/mixins/permissionMixin.js";
import ServiceVisitBoardCardTasks from "@/modules/service-visits/components/ServiceVisitBoardCardTasks.vue";
import {
  isServiceVisitFinished,
  serviceVisitLocationText
} from "../services/service-visit.mapper";

export default {
  name: "ServiceVisitBoardCard",
  mixins: [permissionMixin],
  components: {
    ServiceVisitBoardCardTasks
  },
  props: {
    visit: {
      type: Object,
      required: true
    },
    itemStatuses: {
      type: Array,
      default: () => []
    },
    canMove: {
      type: Boolean,
      default: false
    },
    isMoving: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    machineName() {
      return this.visit.machine
        ? this.visit.machine.serviceDisplayName || this.visit.machine.name || "-"
        : "-";
    },
    formattedDateRange() {
      return `${this.formatShortDate(this.visit.dateFrom)} - ${this.formatShortDate(this.visit.dateTo)}`;
    },
    priorityColor() {
      if (isServiceVisitFinished(this.visit)) {
        return "#7e8299";
      }

      return this.visit.priority && this.visit.priority.slug === "emergency"
        ? "#f64e60"
        : "#3699ff";
    },
    locationText() {
      return serviceVisitLocationText(
        this.visit,
        this.$t("FORM_LABELS.IS_ON_ROAD")
      );
    },
    mechanicNames() {
      return (this.visit.mechanics || [])
        .map(mechanic => `${mechanic.firstName || ""} ${mechanic.lastName || ""}`.trim())
        .join(", ");
    },
    canEditVisit() {
      return (
        !isServiceVisitFinished(this.visit) &&
        this.checkIfUserHasPermission(["service-visits-update"])
      );
    },
    canEditTasks() {
      if (isServiceVisitFinished(this.visit)) {
        return false;
      }

      return Boolean(this.visit.canManageBoard);
    },
    canManageTaskStructure() {
      return Boolean(this.visit.canManageTaskStructure);
    }
  },
  methods: {
    formatShortDate(value) {
      if (!value) {
        return "-";
      }

      const [, month, day] = String(value).split("-");

      return month && day ? `${day}/${month}` : value;
    }
  }
};
</script>

<style scoped>
.visit-card__priority-dot {
  height: 8px;
  min-height: 8px;
  min-width: 8px;
  width: 8px;
}

.visit-card__drag-grip {
  align-items: center;
  align-self: flex-start;
  appearance: none;
  background: transparent;
  border: 0;
  color: #7e8299;
  cursor: grab;
  display: inline-flex;
  flex: 0 0 auto;
  justify-content: center;
  margin: -0.25rem 0.5rem -0.25rem -0.25rem;
  min-height: 1.75rem;
  min-width: 1.5rem;
  padding: 0;
  touch-action: none;
  user-select: none;
}

.visit-card__drag-grip:active {
  cursor: grabbing;
}

.visit-card__drag-grip:focus-visible {
  border-radius: 0.25rem;
  outline: 2px solid #3699ff;
  outline-offset: 2px;
}

.visit-card__drag-grip--disabled {
  cursor: wait;
  opacity: 0.55;
}
</style>
